import client from "@/api/openapi-ts";
import { defineStore, acceptHMRUpdate } from "pinia";
import { ref } from "vue";
import { usePlugins } from "./helpers/usePlugins";
import { toTitleCase } from "@/stores/helpers/helpers";
import { getGeoType } from "@/helpers/geoTypes";
import type { components } from "@/api/schema.d.ts";
import { useGeographyStore } from "@/stores/GeographyStore";
import { useErrorStore } from "@/stores/ErrorStore";

type schemas = components["schemas"];

type Loading = {
  demographicTypes: boolean;
  geography: boolean;
  kpiHistograms: boolean;
  surveyKpiHistograms: boolean;
  kpiHistogram: boolean;
  preview: boolean;
  heatmap: boolean;
  counts: boolean;
  geographyCount: boolean;
  tvMonths: boolean;
  callsigns: boolean;
  networks: boolean;
  tvPrograms: boolean;
  programTimes: boolean;
  createDraft: boolean;
  createAudience: boolean;
  updateAudience: boolean;
  updateDraft: boolean;
  aiAssist: boolean;
  aiNameDescription: boolean;
};

type Errors = {
  kpiHistograms: string | undefined;
  surveyKpiHistograms: string | undefined;
};

type TaskIds = {
  kpiHistograms: string | undefined;
  surveyKpiHistograms: string | undefined;
  kpiHistogram: string | undefined;
  preview: string | undefined;
  heatmap: string | undefined;
  counts: string | undefined;
  geographyCount: string | undefined;
  tvMonths: string | undefined;
  callsigns: string | undefined;
  networks: string | undefined;
  tvPrograms: string | undefined;
  programTimes: string | undefined;
  aiAssist: string | undefined;
  aiNameDescription: string | undefined;
  createAudience: string | undefined;
};

export const useAudienceBuilderStore = defineStore("audienceBuilder", () => {
  const { $ws } = usePlugins();
  const loading = ref<Loading>({
    demographicTypes: false,
    geography: false,
    kpiHistograms: false,
    surveyKpiHistograms: false,
    kpiHistogram: false,
    preview: false,
    heatmap: false,
    counts: false,
    geographyCount: false,
    tvMonths: false,
    callsigns: false,
    networks: false,
    tvPrograms: false,
    programTimes: false,
    createDraft: false,
    createAudience: false,
    updateAudience: false,
    updateDraft: false,
    aiAssist: false,
    aiNameDescription: false,
  });
  const taskIds = ref<TaskIds>({
    kpiHistograms: undefined,
    surveyKpiHistograms: undefined,
    kpiHistogram: undefined,
    preview: undefined,
    heatmap: undefined,
    counts: undefined,
    geographyCount: undefined,
    tvMonths: undefined,
    callsigns: undefined,
    networks: undefined,
    tvPrograms: undefined,
    programTimes: undefined,
    aiAssist: undefined,
    aiNameDescription: undefined,
    createAudience: undefined,
  });
  const errors = ref<Errors>({
    kpiHistograms: undefined,
    surveyKpiHistograms: undefined,
  });
  const demographicTypes = ref<schemas["AudienceBuilderDemographicType"][]>([]);
  const kpiHistograms = ref<
    schemas["AudienceBuilderSurveyKpiHistogramsResponse"][] | undefined
  >(undefined);
  const surveyKpiHistograms = ref<
    schemas["AudienceBuilderSurveyKpiHistogramsResponse"][] | undefined
  >(undefined);
  const kpiHistogram = ref<schemas["AudienceBuilderKpi"]>();
  const preview = ref<any>(null);
  const heatmap = ref<schemas["AudienceBuilderHeatmapResponse"] | null>(null);
  const counts = ref<number>(-1);
  const geographyCount = ref<number | null>(null);
  const mediaMixTypes = ref<any[]>([]);
  const tvMonths = ref<schemas["AudienceBuilderTvProgramViewershipMonths"]>();
  const callsigns = ref<schemas["AudienceBuilderCallsign"][]>([]);
  const networks = ref<schemas["AudienceBuilderNetworks"][]>([]);
  const tvPrograms = ref<schemas["AudienceBuilderTopPrograms"][]>([]);
  const programTimes = ref<
    schemas["AudienceBuilderTvProgramViewershipTimes"][]
  >([]);
  const aiAssist = ref<schemas["AudienceBuilderAiAssistResponse"] | null>(null);
  const aiNameDescription = ref<
    schemas["AudienceBuilderNameAndDescriptionResponse"] | null
  >(null);

  return {
    loading,
    taskIds,
    errors,
    demographicTypes,
    kpiHistograms,
    surveyKpiHistograms,
    kpiHistogram,
    preview,
    heatmap,
    counts,
    geographyCount,
    mediaMixTypes,
    tvMonths,
    callsigns,
    networks,
    tvPrograms,
    programTimes,
    aiAssist,
    aiNameDescription,

    reset() {
      counts.value = -1;
    },
    prettifyGeo(geography: schemas["Geography"]) {
      if (!geography || geography.type === "national") return;
      const geographyStore = useGeographyStore();
      const state = geographyStore.states.find(
        (state) => state.state_id === geography?.state_id,
      );
      if (!state) return geography.name;
      switch (getGeoType(geography.type ?? "")) {
        case "counties":
          return `${geography.name} ${geography.descriptor}, ${state?.name}`;
        case "cities":
          return `${toTitleCase(geography.name!)}, ${state?.name}`;
        case "congressional_district_nextelections":
        case "congressional_districts":
          return `${state?.name} - CD ${geography.name}`;
        case "senate_district_nextelections":
        case "senate_districts":
          return `${state?.name} - State Senate ${geography.name}`;
        case "legislative_district_nextelections":
        case "legislative_districts":
          return `${state?.name} - State Legislature ${geography.name}`;
        case "zip_codes":
          return `${geography.name}, ${state?.name}`;
        default:
          return geography.name;
      }
    },
    async loadDemographicTypes() {
      loading.value.demographicTypes = true;
      try {
        const response = await client.GET(
          "/api/v1/audience_builder/demographic_types",
        );
        demographicTypes.value =
          response.data as schemas["AudienceBuilderDemographicType"][];
      } catch (error) {
        console.error(error);
      } finally {
        loading.value.demographicTypes = false;
      }
    },
    async loadKpiHistograms() {
      loading.value.kpiHistograms = true;
      try {
        const response = await client.GET(
          "/api/v1/audience_builder/kpi_histograms",
        );
        taskIds.value.kpiHistograms = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value.kpiHistograms = false;
      }
    },
    async loadSurveyKpiHistograms(surveyId: number) {
      loading.value.surveyKpiHistograms = true;
      try {
        const response = await client.GET(
          `/api/v1/audience_builder/{survey_id}/kpi_histograms`,
          {
            params: {
              path: {
                survey_id: surveyId,
              },
            },
          },
        );
        taskIds.value.surveyKpiHistograms = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value.surveyKpiHistograms = false;
      }
    },
    async getKpiHistogram(
      payload: schemas["AudienceBuilderKpiHistogramRequest"],
    ) {
      loading.value.kpiHistogram = true;
      try {
        const response = await client.POST(
          "/api/v1/audience_builder/kpi_histogram",
          {
            body: payload,
          },
        );
        taskIds.value.kpiHistogram = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value.kpiHistogram = false;
      }
    },
    async getPreview(payload: schemas["AudienceBuilderPreviewRequest"]) {
      loading.value.preview = true;
      try {
        const response = await client.POST("/api/v1/audience_builder/preview", {
          body: payload,
        });
        taskIds.value.preview = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value.preview = false;
      }
    },
    async getHeatMap(payload: schemas["AudienceBuilderHeatmapRequest"]) {
      loading.value.heatmap = true;
      try {
        const response = await client.POST("/api/v1/audience_builder/heatmap", {
          body: payload,
        });
        taskIds.value.heatmap = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getCounts(payload: schemas["AudienceBuilderCountsRequest"]) {
      loading.value.counts = true;
      try {
        const response = await client.POST("/api/v1/audience_builder/counts", {
          body: payload,
        });
        taskIds.value.counts = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value.counts = false;
      }
    },
    async getGeographyCounts(
      payload?: schemas["AudienceBuilderGeographyCountsRequest"],
    ) {
      loading.value.geographyCount = true;
      try {
        const response = await client.POST(
          "/api/v1/audience_builder/geography_counts",
          {
            body: payload ?? { sync: false, geographies: null },
          },
        );
        taskIds.value.geographyCount = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value.geographyCount = false;
      }
    },
    async getMediaMixTypes() {
      try {
        const response = await client.GET("/api/v1/media_mix_types");
        mediaMixTypes.value = response.data ?? [];
      } catch (error) {
        console.error(error);
      }
    },
    async getTvMonths() {
      loading.value.tvMonths = true;
      try {
        const response = await client.GET(
          "/api/v1/audience_builder/tv_program_viewership_months",
        );
        taskIds.value.tvMonths = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getCallsigns(payload: schemas["AudienceBuilderGetCallsignsRequest"]) {
      loading.value.callsigns = true;
      try {
        const response = await client.POST(
          "/api/v1/audience_builder/callsigns",
          {
            body: payload,
          },
        );
        taskIds.value.callsigns = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getNetworks(payload: schemas["AudienceBuilderGetNetworksRequest"]) {
      loading.value.networks = true;
      try {
        const response = await client.POST(
          "/api/v1/audience_builder/networks",
          {
            body: payload,
          },
        );
        taskIds.value.networks = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getTopPrograms(
      payload: schemas["AudienceBuilderGetTopProgramsRequest"],
    ) {
      loading.value.tvPrograms = true;
      try {
        const response = await client.POST(
          "/api/v1/audience_builder/top_programs",
          {
            body: payload,
          },
        );
        taskIds.value.tvPrograms = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getProgramTimes(
      payload: schemas["AudienceBuilderGetTvProgramViewershipTimes"],
    ) {
      loading.value.programTimes = true;
      try {
        const response = await client.POST(
          "/api/v1/audience_builder/tv_program_viewership_times",
          {
            body: payload,
          },
        );
        taskIds.value.programTimes = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getAiAssist(payload: schemas["AudienceBuilderAiAssistRequest"]) {
      loading.value.aiAssist = true;
      try {
        const response = await client.POST(
          "/api/v1/audience_builder/ai_assist",
          {
            body: payload,
          },
        );
        taskIds.value.aiAssist = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getAiNameDescription(
      payload: schemas["AudienceBuilderNameAndDescriptionRequest"],
    ) {
      loading.value.aiNameDescription = true;
      try {
        const response = await client.POST(
          "/api/v1/audience_builder/ai_name_generator",
          {
            body: payload,
          },
        );
        taskIds.value.aiNameDescription = response.data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "AudienceBuilder",
            payload: { id: response.data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    async createAudience(
      payload: schemas["AudienceBuilderCreateAudienceRequest"],
      draft: boolean = false,
      response_fields?: string[],
    ) {
      loading.value.createAudience = !draft;
      loading.value.createDraft = draft;
      try {
        const response = await client.POST(
          "/api/v1/audience_builder/audience",
          {
            body: {
              ...payload,
              is_draft: draft,
              ...(response_fields && { response_fields }),
            },
          },
        );
        if (response.error) {
          const message =
            response.error.message ||
            "There was a problem creating the audience";
          throw message;
        }
        if (response.data?.task_id) {
          taskIds.value.createAudience = response.data.task_id;
          if ($ws) {
            $ws.subscribe({
              channel: "AudienceBuilder",
              payload: { id: response.data.task_id },
            });
          }
        }
        return response;
      } catch (e) {
        console.error("createAudience failed", e);
        const errorStore = useErrorStore();
        errorStore.showError(String(e));
        throw e;
      } finally {
        loading.value.createAudience = false;
        loading.value.createDraft = false;
      }
    },
    async updateAudience(
      payload: schemas["AudienceBuilderUpdateAudienceRequest"],
    ) {
      if (payload.is_draft) {
        loading.value.updateDraft = true;
      } else {
        loading.value.updateAudience = true;
      }
      try {
        const response = await client.PUT("/api/v1/audience_builder/audience", {
          body: payload,
        });
        if (response.error) {
          throw response.error.message;
        }
        return response.data;
      } catch (error) {
        const errorStore = useErrorStore();
        errorStore.showError(String(error));
        throw error;
      } finally {
        loading.value.updateAudience = false;
        loading.value.updateDraft = false;
      }
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useAudienceBuilderStore, import.meta.hot),
  );
}
